import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Package, AlertCircle, Loader2, ChevronRight } from 'lucide-react';
import { ItemsDto } from '../../../../types/items';
import { itemsService } from '../../../../services/itemsService';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { BackButton } from '../../../common/BackButton';
import { ScanTextInput } from '../../../common/inputs';

interface ItemCardSearchProps {
  onSelect: (itemNo: string) => void;
  onBack?: () => void;
}

// Fritekstsøket bruker LIKE '%x%' i flere kolonner — 3 tegn og maks 50 treff holder det raskt.
// Enter med fullt varenr/strekkode går uansett rett til kortet.
const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 300;
const MAX_RESULTS = 50;

/**
 * Søk/skann vare. Skanning skjer KUN i dette fokuserte feltet (ingen global scanner-hook) —
 * se «Skanning — forsiktighetsregler» i Plan-Varekort-v2.md.
 * Enter: eksakt strekkode (inkl. alternative koder) eller eksakt varenr → rett til varekortet.
 */
export const ItemCardSearch: React.FC<ItemCardSearchProps> = ({ onSelect, onBack }) => {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [items, setItems] = useState<ItemsDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [notFoundTerm, setNotFoundTerm] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchIdRef = useRef(0);

  const runSearch = useCallback(async (value: string): Promise<ItemsDto[]> => {
    const id = ++searchIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await itemsService.searchCurrentItems(value, null, MAX_RESULTS);
      if (id !== searchIdRef.current) return result;
      setItems(result);
      setHasSearched(true);
      return result;
    } catch (err) {
      if (id === searchIdRef.current) {
        console.error('Error searching items:', err);
        setError(t('warehouse.modules.itemCard.search.error', {}, 'Kunne ikke søke etter varer'));
      }
      return [];
    } finally {
      if (id === searchIdRef.current) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const value = term.trim();
    if (value.length < MIN_SEARCH_LENGTH) {
      searchIdRef.current++;
      setItems([]);
      setHasSearched(false);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => { void runSearch(value); }, DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [term, runSearch]);

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const value = term.trim();
    if (!value) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setNotFoundTerm(null);

    try {
      const byBarcode = await itemsService.resolveBarcode(value);
      if (byBarcode.length === 1) {
        onSelect(byBarcode[0].itemNo);
        return;
      }
    } catch (err) {
      // Faller tilbake til fritekstsøk
      console.warn('Barcode resolve failed, falling back to search:', err);
    }

    const result = await runSearch(value);
    const exact = result.filter(i => i.itemNo.toLowerCase() === value.toLowerCase());
    if (exact.length === 1) {
      onSelect(exact[0].itemNo);
    } else if (result.length === 1) {
      onSelect(result[0].itemNo);
    } else if (result.length === 0) {
      setNotFoundTerm(value);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          {onBack && <BackButton onClick={onBack} />}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Package className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                {t('warehouse.modules.itemCard.title', {}, 'Varekort')}
              </h1>
            </div>
            <p className="text-sm text-gray-600">
              {t('warehouse.modules.itemCard.description', {}, 'Skann eller søk opp en vare for å se lager, oppgaver og historikk')}
            </p>
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
          <ScanTextInput
            value={term}
            onChange={(e) => { setTerm(e.target.value); setNotFoundTerm(null); }}
            onKeyDown={handleKeyDown}
            placeholder={t('warehouse.modules.itemCard.search.placeholder', {}, 'Skann strekkode eller søk på varenr/beskrivelse...')}
            autoFocus
            className="w-full pl-10 pr-10 py-3 min-h-[44px] text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin text-blue-500" />}
        </div>

        {notFoundTerm && (
          <p className="mt-3 text-sm text-orange-600">
            {t('warehouse.modules.itemCard.search.notFound', { '0': notFoundTerm }, `Fant ingen vare for «${notFoundTerm}»`)}
          </p>
        )}
        {error && (
          <div className="mt-3 flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {hasSearched && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {items.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-10">
              {t('warehouse.modules.itemCard.search.noResults', {}, 'Ingen varer funnet')}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {items.map(item => (
                <li key={`${item.companyNo}-${item.itemNo}`}>
                  <button
                    type="button"
                    onClick={() => onSelect(item.itemNo)}
                    className="w-full flex items-center gap-3 px-4 py-3 min-h-[56px] text-left hover:bg-gray-50 active:bg-gray-100"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 break-words">{item.itemNo}</div>
                      <div className="text-sm text-gray-600 break-words">{item.description1 || '-'}</div>
                      {item.itemBarCode && <div className="text-xs text-gray-500 font-mono mt-0.5">{item.itemBarCode}</div>}
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
